const NATIVE_MEDIA_UPLOAD_REQUEST = 'NATIVE_MEDIA_UPLOAD_REQUEST'
const NATIVE_MEDIA_UPLOAD_RESPONSE = 'NATIVE_MEDIA_UPLOAD_RESPONSE'
const NATIVE_MEDIA_UPLOAD_PROGRESS = 'NATIVE_MEDIA_UPLOAD_PROGRESS'
const WATCHDOG_TIMEOUT_MS = 30000
const CAPTURE_TIMEOUT_MS = 600000
const MAX_DURATION_LIMIT = 600 // 10 minutes max video recording
const MAX_FILE_SIZE_LIMIT = 500 * 1024 * 1024 // 500MB max file size
const VALID_MODES = ['photo', 'video', 'media', 'file']

function isValidUploadUrl (uploadUrl: string | undefined): boolean {
    if (!uploadUrl) return false
    try {
        const url = new URL(uploadUrl)
        return url.protocol === 'https:'
    } catch {
        return false
    }
}

let pendingCapture: { reject: (err: Error) => void; cleanup: () => void } | null = null

/**
 * Forwards media capture/upload requests from tool iframes to the React Native native layer.
 *
 * Modes: 'photo' | 'video' | 'media' | 'file'
 * Params: { mode, uploadUrl, maxDuration?, allowedTypes?, maxFileSize? }
 * Returns: { uploaded: true, fileName } on success, null on cancel, rejects on error/timeout.
 *
 * Security: validates uploadUrl (HTTPS only), checks event.origin on responses.
 * Timeout: capture phase uses maxDuration + 60s (or 10 min default), upload phase uses 30s watchdog.
 */
export async function nativeMediaUploadListener (message: any): Promise<any> {
    const params = (message && message.data) || {}
    const mode = params.mode || 'photo'
    const uploadUrl = params.uploadUrl
    const maxDuration = params.maxDuration

    if (!VALID_MODES.includes(mode)) {
        return Promise.reject(new Error(`Invalid mode: '${mode}'. Must be one of: ${VALID_MODES.join(', ')}`))
    }

    if (!isValidUploadUrl(uploadUrl)) {
        return Promise.reject(new Error('Invalid or missing uploadUrl: must be a valid HTTPS URL'))
    }

    const safeDuration = (typeof maxDuration === 'number' && isFinite(maxDuration) && maxDuration > 0)
        ? Math.min(maxDuration, MAX_DURATION_LIMIT)
        : undefined

    const safeMaxFileSize = (typeof params.maxFileSize === 'number' && params.maxFileSize > 0)
        ? Math.min(params.maxFileSize, MAX_FILE_SIZE_LIMIT)
        : undefined

    if (pendingCapture) {
        pendingCapture.reject(new Error('Media capture cancelled'))
        pendingCapture.cleanup()
        pendingCapture = null
    }

    return new Promise((resolve, reject) => {
        let watchdogId: ReturnType<typeof setTimeout>

        const resetWatchdog = (timeout = WATCHDOG_TIMEOUT_MS) => {
            clearTimeout(watchdogId)
            watchdogId = setTimeout(() => {
                cleanup()
                reject(new Error('Media upload timed out — no response from native'))
            }, timeout)
        }

        const handler = (event: MessageEvent) => {
            // RN WebView injectJavaScript messages may arrive with empty origin
            if (event.origin !== '' && event.origin !== window.location.origin) return

            let data = event.data
            if (typeof data === 'string') {
                try { data = JSON.parse(data) } catch { return }
            }

            if (data?.type === NATIVE_MEDIA_UPLOAD_PROGRESS) {
                if (data.status === 'uploading') {
                    resetWatchdog()
                }
            }

            if (data?.type === NATIVE_MEDIA_UPLOAD_RESPONSE) {
                cleanup()
                if (data.success) {
                    resolve(data.data)
                } else if (data.error === 'cancelled') {
                    resolve(null)
                } else {
                    reject(new Error(data.error || 'Media capture failed'))
                }
            }
        }

        const cleanup = () => {
            window.removeEventListener('message', handler)
            clearTimeout(watchdogId)
            pendingCapture = null
        }

        window.addEventListener('message', handler)
        pendingCapture = { reject, cleanup }

        if ((window as any).ReactNativeWebView) {
            (window as any).ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: NATIVE_MEDIA_UPLOAD_REQUEST,
                    mode,
                    ...(safeDuration != null && { maxDuration: safeDuration }),
                    uploadUrl,
                    ...(params.allowedTypes && { allowedTypes: params.allowedTypes }),
                    ...(safeMaxFileSize != null && { maxFileSize: safeMaxFileSize }),
                }),
            )
            const captureTimeout = safeDuration
                ? (safeDuration + 60) * 1000
                : CAPTURE_TIMEOUT_MS
            resetWatchdog(captureTimeout)
        } else {
            cleanup()
            reject(new Error('Native camera not available — ReactNativeWebView not found'))
            return
        }
    })
}
